import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, Calendar, Globe, Plus, ShieldCheck, Users } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { z } from 'zod';
import { PayloadApiError, useCreateCustomer, useCustomer, useUpdateCustomer } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { UserManagement } from '@/features/shared/users';
import { CustomerDetailsFields } from '@/shared/components/customer/customer-details-fields';
import { CustomerQuotaCard } from '@/shared/components/customer/customer-quota-card';
import { DetailCard, DetailPageHeader, InfoCard, PageContainer } from '@/shared/components/layout';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/shared/components/ui';

import { customerFormSchema } from '../schema';
import type { CustomerFormValues } from '../types';

export default function ManageCustomer() {
  const { t } = useIntl();
  const { user: currentUser } = useAuth();
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const { data: customer } = useCustomer(id || '');
  const isGmnAdmin = currentUser?.role === 'gmn-admin';
  const [usage, setUsage] = useState({ userCount: 0, inviteCount: 0 });
  const [userQuotaInput, setUserQuotaInput] = useState('50');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  const form = useForm<z.input<typeof customerFormSchema>, any, CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      name: '',
      companyType: '',
      industry: '',
      website: '',
      userQuota: 50,
    },
  });
  const userQuotaValue = Number(form.watch('userQuota') ?? 0);
  const totalUsage = usage.userCount + usage.inviteCount;
  const quotaForUsage = isEditMode ? (customer?.userQuota ?? userQuotaValue) : userQuotaValue;
  const handleUsageChange = useCallback((nextUsage: typeof usage) => {
    setUsage(nextUsage);
  }, []);

  const lastSyncedCustomerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isEditMode || !customer) {
      return;
    }

    const signature = `${customer.id}:${customer.updatedAt ?? ''}:${customer.userQuota ?? ''}`;
    if (lastSyncedCustomerRef.current === signature) {
      return;
    }

    const nextQuota = customer.userQuota || 50;
    form.reset({
      name: customer.name,
      companyType: customer.companyType || '',
      industry: customer.industry || '',
      website: customer.website || '',
      userQuota: nextQuota,
    });
    setUserQuotaInput(String(nextQuota));
    lastSyncedCustomerRef.current = signature;
  }, [isEditMode, customer, form]);

  useEffect(() => {
    if (isEditMode) return;
    const currentValue = form.getValues('userQuota');
    if (typeof currentValue === 'number' && !Number.isNaN(currentValue)) {
      setUserQuotaInput(String(currentValue));
    } else {
      setUserQuotaInput('');
    }
  }, [isEditMode, form]);

  const handleQuotaValidationErrors = (error: unknown) => {
    if (!(error instanceof PayloadApiError)) return false;
    const messages =
      error.response?.errors?.map((err) => {
        if (typeof err === 'object' && err && 'message' in err) {
          return String((err as { message: string }).message);
        }
        return '';
      }) ?? [];

    if (messages.includes('CustomerQuotaBelowMinimum')) {
      form.setError('userQuota', {
        type: 'manual',
        message: t('errors.quotaBelowMinimum'),
      });
      return true;
    }

    if (messages.includes('CustomerQuotaBelowActiveUsers')) {
      form.setError('userQuota', {
        type: 'manual',
        message: t('errors.quotaBelowActiveUsers'),
      });
      return true;
    }

    return false;
  };

  const handleSubmit = async (data: CustomerFormValues) => {
    if (typeof data.userQuota !== 'number' || Number.isNaN(data.userQuota) || data.userQuota < 5) {
      form.setError('userQuota', {
        type: 'manual',
        message: t('errors.quotaBelowMinimum'),
      });
      return;
    }

    if (!isGmnAdmin) return;

    try {
      if (isEditMode) {
        await updateCustomer.mutateAsync({ id, data });
        toast.success(t('customers.customerUpdatedSuccessfully'));
      } else {
        const createdCustomer = await createCustomer.mutateAsync(data);
        toast.success(t('customers.customerCreatedSuccessfully'));

        navigate(`/gmn/customer-management/customers/${createdCustomer.doc.id}`);
      }
    } catch (error) {
      const handled = handleQuotaValidationErrors(error);
      if (!handled) {
        toast.error(t('customers.errorSavingCustomer'));
      }
    }
  };

  // Build meta items for header
  const metaItems =
    isEditMode && customer
      ? [
          ...(customer.industry ? [{ icon: Building2, label: customer.industry }] : []),
          ...(customer.website
            ? [
                {
                  icon: Globe,
                  label: customer.website.replace('https://', ''),
                  href: customer.website,
                },
              ]
            : []),
          ...(customer.createdAt
            ? [
                {
                  icon: Calendar,
                  label: `${t('common.since')} ${DateTime.fromISO(customer.createdAt).toFormat('dd.MM.yyyy')}`,
                },
              ]
            : []),
        ]
      : undefined;

  // Avatar content with initials
  const avatarContent =
    isEditMode && customer ? (
      <span className="text-3xl font-bold">{customer.name.substring(0, 2).toUpperCase()}</span>
    ) : undefined;

  return (
    <PageContainer animation="slide-right">
      <DetailPageHeader
        backHref="/gmn/customer-management/customers"
        backLabel={t('customers.backToOverview')}
        title={isEditMode && customer ? customer.name : t('customers.newCustomer')}
        avatarContent={avatarContent}
        isCreateMode={!isEditMode}
        metaItems={metaItems}
        subtitle={!isEditMode ? t('customers.createNewCustomerHint') : undefined}
        secondaryAction={{
          label: t('common.discard'),
          onClick: () => navigate('/gmn/customer-management/customers'),
        }}
        primaryAction={{
          label: isEditMode
            ? updateCustomer.isPending
              ? t('common.updating')
              : t('common.update')
            : createCustomer.isPending
              ? t('common.creating')
              : t('common.create'),
          onClick: form.handleSubmit(handleSubmit),
          disabled:
            (isEditMode ? updateCustomer.isPending : createCustomer.isPending) || !isGmnAdmin,
          loading: isEditMode ? updateCustomer.isPending : createCustomer.isPending,
        }}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content Column (2/3) */}
            <div className="lg:col-span-2 space-y-8">
              {/* General Info Card */}
              <DetailCard icon={Building2} title={t('customers.customerDetails')}>
                <CustomerDetailsFields
                  control={form.control}
                  disabled={!isGmnAdmin}
                  labels={{
                    name: t('common.name'),
                    companyType: t('customers.companyType'),
                    industry: t('customers.industry'),
                    website: t('customers.website'),
                  }}
                  placeholders={{
                    name: t('customers.companyNamePlaceholder'),
                    companyType: t('customers.companyTypePlaceholder'),
                    industry: t('customers.industryPlaceholder'),
                    website: 'https://example.com',
                  }}
                  websiteWithIcon
                />
              </DetailCard>
            </div>

            {/* Sidebar Column (1/3) */}
            <div className="space-y-6">
              {/* Quota Card */}
              <CustomerQuotaCard
                icon={ShieldCheck}
                title={t('customers.licenseAndQuota')}
                usageLabel={t('customers.userLicenses')}
                totalUsage={totalUsage}
                quota={quotaForUsage}
                quotaControl={
                  <FormField
                    control={form.control}
                    name="userQuota"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>{t('customers.maxUserCount')}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            disabled={!isGmnAdmin}
                            value={userQuotaInput}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setUserQuotaInput(nextValue);
                              if (nextValue === '') {
                                field.onChange(undefined);
                                return;
                              }
                              const parsed = Number(nextValue);
                              field.onChange(Number.isNaN(parsed) ? undefined : parsed);
                            }}
                            onBlur={(event) => {
                              const value = event.target.value;
                              setUserQuotaInput(value);
                              if (value === '') {
                                field.onChange(undefined);
                                form.setError('userQuota', {
                                  type: 'manual',
                                  message: t('errors.quotaBelowMinimum'),
                                });
                                return;
                              }
                              const parsedValue = Number(value);
                              if (Number.isNaN(parsedValue)) {
                                field.onChange(undefined);
                                form.setError('userQuota', {
                                  type: 'manual',
                                  message: t('errors.quotaBelowMinimum'),
                                });
                                return;
                              }
                              field.onChange(parsedValue);
                              if (parsedValue < 5) {
                                form.setError('userQuota', {
                                  type: 'manual',
                                  message: t('errors.quotaBelowMinimum'),
                                });
                              } else {
                                form.clearErrors('userQuota');
                              }
                            }}
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          {t('customers.userQuotaHelper')}
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                }
              />

              {/* Info Card - Only in Create Mode */}
              {!isEditMode && (
                <InfoCard title={t('common.hint')} variant="info">
                  <p>{t('customers.createCustomerHint')}</p>
                </InfoCard>
              )}
            </div>
          </div>
        </form>
      </Form>

      {/* Users Card - Only in Edit Mode - Full Width */}
      {isEditMode && (
        <DetailCard
          icon={Users}
          title={t('users.users')}
          noPadding
          className="mt-8"
          headerAction={
            isGmnAdmin && (
              <Button variant="outline" size="sm" onClick={() => setIsInviteDialogOpen(true)}>
                <Plus className="size-4 mr-2" />
                {t('users.inviteUser')}
              </Button>
            )
          }
        >
          <UserManagement
            allowedRoles={['customer-admin']}
            customerId={id}
            hideInviteButton
            onUsageChange={handleUsageChange}
            embedded
            isInviteDialogOpen={isInviteDialogOpen}
            onInviteDialogOpenChange={setIsInviteDialogOpen}
          />
        </DetailCard>
      )}
    </PageContainer>
  );
}
